/**
 * Servidor autoritativo (M1). Corre `@tak/sim` como única fuente de verdad.
 *
 * Soporta 1 o 2 jugadores. Para el modo online práctico con un solo humano,
 * el slot 1 puede llevar un bot server-side (IA determinista que persigue el
 * balón), de modo que hay rival real sin necesitar dos clientes de red.
 *
 * Propiedad de seguridad clave (anti-speedhack): `step()` avanza EXACTAMENTE un
 * tick. El volumen de inputs que reciba no cambia cuántos ticks simula el
 * mundo. Inputs pasados o demasiado adelantados se descartan.
 */
import {
  GameState, InputState, MapConfig, MatchRules, TeamId,
  KICK_RANGE, POSSESSION_TIME_MAX,
  createGame, createInputState, updateGameMulti,
  setSlotTeam, setActiveSlots, setSlotActive, resetKickoff,
} from "../game";
import { InputCommand, Snapshot, NET_FIXED_DT, MAX_INPUT_LEAD, readBody } from "./protocol";
import { validateInputCommand, sanitizeGameState, MAX_QUEUED_INPUTS } from "./guard";

/** Segundos de posesión que "carga" el bot antes de disparar cada patada. */
const BOT_KICK_CHARGE_S = 0.6;

/**
 * Ticks sin recibir input fresco de un slot tras los cuales se le da input
 * NEUTRO en vez de repetir el último.
 *
 * Repetir el último input es lo correcto durante un hueco corto (jitter o un
 * paquete perdido): el jugador seguía pulsando. Pero si el cliente deja de
 * enviar del todo — se le congela la pestaña en segundo plano, pierde la red o
 * cierra el portátil — repetirlo para siempre significa que su disco sigue
 * corriendo solo por la cancha, empujando el balón y marcando goles sin nadie
 * al mando. Medio segundo es mucho más de lo que dura cualquier hueco de red
 * normal y mucho menos de lo que se tarda en notar un disco fantasma.
 */
const STALE_INPUT_TICKS = 30;

/** Input neutro compartido (no se muta; solo se lee). */
const IDLE_INPUT: InputState = createInputState();

/**
 * Cuánto retraso se le perdona a un input antes de darlo por perdido (ticks).
 *
 * ~100 ms. Por debajo de eso, lo que llega tarde es casi siempre un hipo de red
 * sobre una orden que la persona SÍ dio, y aplicarla un pelín tarde es lo que
 * quiere: que su patada salga. Por encima, ya no es la jugada que estaba
 * pensando y aplicarla sería mover su disco por sorpresa.
 */
const LATE_INPUT_GRACE_TICKS = 6;

/**
 * Re-sella al presente un input que llegó tarde por poco. Devuelve `null` si el
 * retraso es demasiado (o si el comando ni siquiera tiene forma válida).
 */
function salvageLateInput(cmd: InputCommand, serverTick: number): InputCommand | null {
  const raw = cmd as { tick?: unknown } | null;
  if (!raw || typeof raw.tick !== "number" || !Number.isInteger(raw.tick)) return null;
  const lateBy = serverTick - raw.tick;
  if (lateBy <= 0 || lateBy > LATE_INPUT_GRACE_TICKS) return null;
  // Se valida contra el presente: así el input pasa por el mismo saneado que
  // cualquier otro (ver `validateInputCommand`) y entra en el tick actual.
  return validateInputCommand({ tick: serverTick, input: cmd.input }, serverTick, MAX_INPUT_LEAD);
}

export interface ServerOptions {
  /** Número de jugadores (cuerpos). 1 = local; 2+ = online. */
  players?: number;
  /** Equipo por slot (rojo/turquesa); por defecto alterna. */
  teams?: TeamId[];
  map?: MapConfig;
  rules?: MatchRules;
  /** Si true y hay 2 jugadores, el slot 1 lo controla un bot server-side. */
  bot?: boolean;
  /** Slots ocupados al crear la partida. Por defecto todos (ver `setActive`). */
  active?: boolean[];
}

/**
 * IA determinista del bot (slot 1): persigue el balón y lo patea al controlarlo.
 *
 * Con la patada por FLANCO (patear al tocar), un `kick` mantenido no repite: el
 * bot debe PULSAR la tecla. Lo consigue pateando cuando lleva algo de posesión
 * acumulada; como patear resetea la posesión a 0, el tick siguiente `kick` vuelve
 * a `false` y el pulso se re-arma solo. Así el bot "atrapa y dispara" en cadencia.
 */
function botInput(g: GameState): InputState {
  const w = g.world;
  const b = g.ball;
  const p = g.player2;
  const dx = w.px[b] - w.px[p];
  const dy = w.py[b] - w.py[p];
  const dist = Math.hypot(dx, dy);
  const closeToKick = dist < KICK_RANGE;
  const charge = Math.min(BOT_KICK_CHARGE_S, POSSESSION_TIME_MAX);
  return {
    up: dy < -8,
    down: dy > 8,
    left: dx < -8,
    right: dx > 8,
    kick: closeToKick && g.possession[1] >= charge,
    kickRelease: false,
    // El bot juega con "teclas": no simula un stick analógico.
    mx: 0,
    my: 0,
  };
}

export class AuthoritativeServer {
  readonly game: GameState;
  readonly numPlayers: number;
  private tick = 0;
  private buffers: Map<number, InputState>[];
  private lastInputs: InputState[];
  private ackTicks: number[];
  /** Tick del input más nuevo recibido por slot (para reportar `bufAhead`). */
  private maxInputTicks: number[];
  /** Último tick en que se aplicó un input FRESCO de cada slot (ver STALE_INPUT_TICKS). */
  private lastFreshTicks: number[];
  private bot: boolean;
  /** Inputs reales del cliente aplicados en su tick (métrica de calidad de red). */
  appliedInputs = 0;
  /** Inputs rechazados por el guard (SEC-1): tick inválido, tardío o flood. */
  rejectedInputs = 0;
  /** Inputs que llegaron tarde y se rescataron al presente (ver `receiveInput`).
   *  Es una señal de red, no de abuso: si sube mucho, la sala está sufriendo. */
  lateInputs = 0;
  /** Cuerpos recentrados por la red de seguridad de física. Debería quedar en 0. */
  sanityFixes = 0;

  constructor(opts: ServerOptions = {}) {
    this.numPlayers = Math.max(1, Math.floor(opts.players ?? 1));
    this.game = createGame(opts.map, opts.rules, {
      players: this.numPlayers, teams: opts.teams, active: opts.active,
    });
    this.bot = !!opts.bot;
    this.buffers = Array.from({ length: this.numPlayers }, () => new Map<number, InputState>());
    this.lastInputs = Array.from({ length: this.numPlayers }, () => createInputState());
    this.ackTicks = Array.from({ length: this.numPlayers }, () => -1);
    this.maxInputTicks = Array.from({ length: this.numPlayers }, () => -1);
    this.lastFreshTicks = Array.from({ length: this.numPlayers }, () => 0);
  }

  /** Equipo de cada slot (para la lista de jugadores / colores). */
  get teams(): TeamId[] {
    return this.game.teams;
  }

  get currentTick(): number {
    return this.tick;
  }

  get botEnabled(): boolean {
    return this.bot;
  }

  /**
   * Activa el bot server-side en el slot 1 (fallback por timeout: un jugador
   * solo que llevaba demasiado esperando rival humano). Solo tiene efecto con
   * 2 jugadores; desde este tick el slot 1 lo conduce la IA determinista.
   */
  enableBot(): void {
    if (this.numPlayers !== 2) return;
    this.bot = true;
    // El bot ocupa el slot 1 de verdad: si estaba aparcado por vacío, vuelve a
    // la cancha (si no, la IA conduciría un disco fuera del campo).
    setSlotActive(this.game, 1, true);
  }

  /**
   * Encola input de un cliente para su slot. Rechaza lo inaplicable (SEC-1):
   * tick no entero/no finito, del pasado (ya se simuló) o demasiado adelantado
   * (speedhack). El input se normaliza a seis booleanos estrictos antes de
   * tocar la cola, así el sim nunca ve un `up: 1e9`.
   */
  receiveInput(cmd: InputCommand, slot = 0): boolean {
    if (!Number.isInteger(slot) || slot < 0 || slot >= this.numPlayers) return false;
    let clean = validateInputCommand(cmd, this.tick, MAX_INPUT_LEAD);
    if (!clean) {
      // ¿Es un input que llegó TARDE por poco? Se re-sella al presente en vez de
      // tirarlo. La diferencia se nota sobre todo en la patada: el jugador la
      // pulsa, su cliente la predice, el paquete se retrasa dos ticks por un
      // hipo de red y el servidor lo descartaba — el balón "no salía" y encima
      // la reconciliación se lo devolvía a los pies. Aplicarlo 30 ms tarde es
      // infinitamente mejor que no aplicarlo, y no debilita el anti-speedhack:
      // `step()` sigue avanzando un tick por tick pase lo que pase, así que
      // nadie simula de más — como mucho, su orden llega un pelo tarde.
      const late = salvageLateInput(cmd, this.tick);
      if (!late) { this.rejectedInputs++; return false; }
      clean = late;
      this.lateInputs++;
    }
    const buf = this.buffers[slot];
    // Ticks que ya pasaron sin consumirse (el cliente los mandó y el servidor
    // avanzó sin ellos): se purgan aquí para que la cola no crezca sin techo.
    if (buf.size >= MAX_QUEUED_INPUTS) {
      for (const t of buf.keys()) if (t < this.tick) buf.delete(t);
      if (buf.size >= MAX_QUEUED_INPUTS) { this.rejectedInputs++; return false; }
    }
    buf.set(clean.tick, clean.input);
    if (clean.tick > this.maxInputTicks[slot]) this.maxInputTicks[slot] = clean.tick;
    return true;
  }

  /**
   * Inputs encolados de un slot. Es la superficie de memoria que un cliente
   * puede reservar en el servidor: queda acotada por la ventana viva
   * (`MAX_INPUT_LEAD`) y, como red de seguridad, por `MAX_QUEUED_INPUTS`.
   */
  queuedInputs(slot: number): number {
    return this.buffers[slot]?.size ?? 0;
  }

  private consume(slot: number): InputState {
    const queued = this.buffers[slot].get(this.tick);
    if (queued) {
      this.lastInputs[slot] = queued;
      this.ackTicks[slot] = this.tick;
      this.lastFreshTicks[slot] = this.tick;
      this.buffers[slot].delete(this.tick);
      if (slot === 0) this.appliedInputs++;
      return queued;
    }
    // Hueco corto: se repite el último input (el jugador seguía pulsando).
    // Hueco largo: el cliente no está mandando nada, así que su disco se queda
    // quieto en vez de seguir corriendo solo (ver STALE_INPUT_TICKS).
    if (this.tick - this.lastFreshTicks[slot] > STALE_INPUT_TICKS) return IDLE_INPUT;
    return this.lastInputs[slot];
  }

  /** ¿Este slot lleva demasiado sin mandar input? (cliente congelado o caído). */
  isStale(slot: number): boolean {
    if (slot < 0 || slot >= this.numPlayers) return false;
    return this.tick - this.lastFreshTicks[slot] > STALE_INPUT_TICKS;
  }

  /** Avanza un único tick autoritativo. */
  step(): void {
    const inputs: InputState[] = [];
    for (let slot = 0; slot < this.numPlayers; slot++) {
      // Bot server-side legacy: solo en 2 jugadores conduce el slot 1.
      if (slot === 1 && this.bot && this.numPlayers === 2) inputs.push(botInput(this.game));
      else inputs.push(this.consume(slot));
    }
    updateGameMulti(this.game, inputs, NET_FIXED_DT);
    // Red de seguridad (SEC-1 · sanity): un NaN en un cuerpo contamina a todos en
    // el siguiente step y deja la sala inservible para ambos jugadores. Barrer
    // ~20 cuerpos por tick es ruido frente al solver, y el contador delata el bug.
    this.sanityFixes += sanitizeGameState(this.game);
    this.tick++;
  }

  /** Mueve un slot de equipo (arrastre de admin). Reposiciona con saque nuevo. */
  setTeam(slot: number, team: TeamId): boolean {
    return setSlotTeam(this.game, slot, team);
  }

  /**
   * Sincroniza qué slots tienen a alguien dentro.
   *
   * Los vacíos se aparcan fuera del campo y dejan de colisionar: los sitios
   * libres de una sala no son jugadores de relleno. Con bot server-side, el
   * slot 1 cuenta como ocupado (lo conduce la IA).
   */
  setActive(occupied: readonly boolean[]): boolean {
    const active = Array.from({ length: this.numPlayers }, (_, slot) =>
      (this.bot && this.numPlayers === 2 && slot === 1) || occupied[slot] === true);
    return setActiveSlots(this.game, active);
  }

  /**
   * Recoloca a todos como en un saque (sin tocar marcador ni reloj).
   *
   * Lo usa la sala cuando cambia la plantilla ANTES de jugar: el reparto de
   * saque depende de cuánta gente hay, así que el que ya estaba dentro también
   * tiene que recolocarse.
   */
  resetPositions(): void {
    resetKickoff(this.game);
  }

  /**
   * ¿Nadie ha tocado todavía el balón? (sigue quieto en el centro).
   *
   * Es la señal de "aquí aún no se juega": mientras sea cierta, recolocar a la
   * gente no le quita la jugada a nadie. En cuanto la pelota se mueve, mover
   * discos por debajo sería intervenir en un partido en marcha.
   */
  get untouched(): boolean {
    const w = this.game.world;
    const b = this.game.ball;
    const still = Math.hypot(w.vx[b], w.vy[b]) < 1;
    const centered = Math.hypot(w.px[b] - this.game.courtW / 2, w.py[b] - this.game.courtH / 2) < 1;
    return still && centered;
  }

  /** Slots ocupados ahora mismo (lo difunde el snapshot). */
  get activeSlots(): boolean[] {
    return this.game.active.slice();
  }

  /** Termina el partido ya (finalizar por el admin): gana quien va arriba, o empate. */
  endMatch(): void {
    const s = this.game.score;
    this.game.winner = s[0] > s[1] ? 0 : s[1] > s[0] ? 1 : null;
    this.game.matchOver = true;
  }

  snapshot(forSlot = 0): Snapshot {
    const players = this.game.players.map((body) => readBody(this.game, body));
    return {
      serverTick: this.tick,
      ackTick: this.ackTicks[forSlot] ?? -1,
      players,
      active: this.game.active.slice(),
      ball: readBody(this.game, this.game.ball),
      score: [this.game.score[0], this.game.score[1]],
      over: this.game.matchOver,
      winner: this.game.winner,
      matchTime: this.game.matchTime,
      resetTimer: this.game.resetTimer,
      overtime: this.game.overtime,
      kickoff: this.game.kickoffTeam,
      possession: this.game.possession.slice(),
      goalAnim: this.game.goalAnim,
      goalSide: this.game.goalSide,
      // Lead observado del cliente: cuántos ticks por delante del reloj del
      // servidor está su input más nuevo. El cliente lo usa para dimensionar su
      // jitter-buffer sin depender de un lead fijo.
      bufAhead: this.maxInputTicks[forSlot] - this.tick,
    };
  }
}
